import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { createSelectionStore } from '@stores/create-selection-store';
import { pxToRem } from '@utils/converters';

import { BASE_ACTION_TYPE } from 'api/types';

import { SearchEmptyState } from './common';
import {
  ActionGroup,
  ActionItem,
  GroupHeader,
  OnCreateActionSelectProps,
  TabContentWrapper,
} from './common';
import { useActionGroups } from './use-action-groups';

const collapsedGroupsStore = createSelectionStore({
  persistKey: 'add-action-modal-collapsed-groups',
});

type ActionsTabProps = {
  filteredActionTypes: BASE_ACTION_TYPE[];
  skipGroups: string[];
  onSelect: (props: OnCreateActionSelectProps) => void;
  onClearSearch: () => void;
  isDisabled?: boolean;
};

export const ActionsTab = ({
  filteredActionTypes,
  skipGroups,
  onSelect,
  onClearSearch,
  isDisabled,
}: ActionsTabProps) => {
  const { leftColumnGroups, rightColumnGroups } = useActionGroups();

  const filterGroups = (groups: ActionGroup[]) =>
    groups
      .filter((group) => !skipGroups.includes(group.type))
      .map((group) => ({
        ...group,
        actions: group.actions.filter((action) => filteredActionTypes.includes(action.type)),
      }));

  const filteredLeftGroups = filterGroups(leftColumnGroups);
  const filteredRightGroups = filterGroups(rightColumnGroups);

  const totalActions = [...filteredLeftGroups, ...filteredRightGroups].reduce(
    (sum, group) => sum + group.actions.length,
    0
  );
  const hasNoResults = totalActions === 0;

  return (
    <TabContentWrapper>
      {hasNoResults ? (
        <SearchEmptyState onClearSearch={onClearSearch} />
      ) : (
        <ActionsTabContent>
          <Stack space={2}>
            {filteredLeftGroups.map((group) => (
              <ActionGroupSection
                key={group.type}
                group={group}
                onSelect={onSelect}
                isDisabled={isDisabled}
              />
            ))}
          </Stack>
          <Stack space={2}>
            {filteredRightGroups.map((group) => (
              <ActionGroupSection
                key={group.type}
                group={group}
                onSelect={onSelect}
                isDisabled={isDisabled}
              />
            ))}
          </Stack>
        </ActionsTabContent>
      )}
    </TabContentWrapper>
  );
};

type ActionGroupSectionProps = {
  group: ActionGroup;
  onSelect: (props: OnCreateActionSelectProps) => void;
  isDisabled?: boolean;
};

const ActionGroupSection = ({ group, onSelect, isDisabled }: ActionGroupSectionProps) => {
  const { t } = useScopedTranslate(locale);
  const isCollapsed = collapsedGroupsStore.useStore((state) => state[group.type]);

  return (
    <div>
      <GroupHeader
        onClick={() => collapsedGroupsStore.toggle(group.type)}
        isCollapsed={isCollapsed}
        text={t(group.type as keyof typeof locale.en)}
      />
      <Collapse isCollapsed={isCollapsed}>
        <div>
          {group.actions.map((action, index) => (
            <ActionItem
              {...action}
              key={`${group.type}-${index}`}
              onSelect={() =>
                action.isPremium || isDisabled
                  ? null
                  : onSelect({ actionType: action.type, values: action.values })
              }
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

const ActionsTabContent = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: pxToRem(24),
});

const locale = {
  en: {
    send: 'Send',
    documents: 'Documents',
    utilities: 'Utilities',
    flowControl: 'Flow control',
    wait: 'Wait',
    update: 'Update',
  },
};
